/**
 * CountLink (alias: CountChip) — SIH v1.1 (§3.1).
 *
 * A drill count renders as a chip — same anchor / href / deep-link contract
 * as v1 (PD-11 binding: NFR-U1 journey stays green unmodified), but the
 * visual surface now matches proposal §3.1: status dot, count, status word,
 * and the › glyph inside a 44px-tall pill.
 *
 * **Chroming rule (UX-T16 §structural 8 / proposal §3.1):** the status dot
 * is a `::before` pseudo-element and the › glyph is a `::after`
 * pseudo-element. The DOM under the anchor carries ONLY the count, the
 * label, and the visually-hidden scope disambiguator — no new sibling
 * nodes. This keeps the DOM byte-identical to v1's structure for the
 * testid-anchored NFR-U1 Playwright journey.
 *
 * The Von Restorff emphasis: when the underlying count is Failed **and > 0** the
 * chip takes the failed-tint background + failed-colour border + failed-colour
 * count + failed-colour word. All other chips stay quiet. When Failed = 0
 * there is no false alarm.
 *
 * Accessible name (UX-T16 §structural 4): the chip's accessible name is the
 * `{Status} — {count} invoices for {deployment}` shape, so a screen reader
 * user hears "Failed — 18 invoices for 1000117278_002" rather than three
 * disjoint numbers. The scope disambiguator sits in a visually-hidden span
 * so sighted users see only the count + word.
 */

import type { PeriodSelection } from '@domain/types'
import { Link } from './Link'

interface CountLinkProps {
  /** The BR-2 status the list will filter by — the count claims this status (FR-1 counts are claims). */
  count: number
  /** Deployment the list is pre-filtered to. */
  dbName: string
  /** Verb for the accessible name (e.g. "invoices"). */
  noun: 'invoices'
  /** Visual status key for the triad colour (word + colour, never colour alone). */
  statusKey: 'synced' | 'failed' | 'pending'
  statusLabel: string
  period: PeriodSelection
  /**
   * Optional override for the link target. Defaults to
   * `/deployment/{dbName}` — the portfolio branch drill (J1 step 2).
   * Portfolio section rollup chips pass `/tpin/{tin}` so the per-TPIN
   * drill view gets the one-click affordance (proposal §3.8).
   */
  to?: string
  /**
   * Optional override for the `data-testid`. The default
   * `count-${statusKey}` keeps the v1 NFR-U1 testid surface intact;
   * the portfolio section rollup chips override with a TPIN-scoped
   * prefix so the rollup and per-branch chips don't collide on the
   * shared status key (`tpin-rollup-${statusKey}`).
   */
  testid?: string
}

/** Whether the failed chip should take the failed-tint emphasis. */
function shouldEmphasiseFailed(statusKey: 'synced' | 'failed' | 'pending', count: number): boolean {
  return statusKey === 'failed' && count > 0
}

export function CountLink({ count, dbName, noun, statusKey, statusLabel, period, to, testid }: CountLinkProps) {
  const emphasise = shouldEmphasiseFailed(statusKey, count)
  const classes = ['count-link', `count-${statusKey}`, emphasise ? 'is-emphasis' : '']
    .filter((c) => c !== '')
    .join(' ')
  const target = to ?? `/deployment/${encodeURIComponent(dbName)}`
  const testidValue = testid ?? `count-${statusKey}`

  return (
    <Link
      to={target}
      className={classes}
      testid={testidValue}
      period={period}
      ariaLabel={`${statusLabel} — ${count} ${noun} for ${dbName}`}
    >
      <span className="count-value">{count}</span>
      <span className="count-label">{statusLabel}</span>
      <span className="visually-hidden">{`, ${noun} for ${dbName}.`}</span>
    </Link>
  )
}

/** Public re-export for spec parity — the canonical name is CountChip
 * (proposal §3.1) but the existing tests + import sites use CountLink. */
export { CountLink as CountChip }

/**
 * CountStatic — the `.count-static` data variant (UX-T16 §structural 11,
 * proposal §3.1).
 *
 * Counts that **cannot drill** render as plain data — bg status-tint, no
 * border, no chevron, not a link, not focusable. The affordance difference
 * (border + chevron = link, tint-only = data) is geometry, not colour alone
 * (proposal §4 row "Count chip, data"). Used for the TPIN-view rollups
 * (proposal §3.9): the per-TPIN counts on the /tpin/{tin} page are
 * rendered as `.count-static` because no TPIN-scoped invoice list exists
 * (invoice endpoints are deployment-scoped; F1 fitness).
 *
 * Conditional emphasis mirrors the link chip's Von Restorff rule — failed
 * counts > 0 take the failed-tint + outline (so a TPIN with failing
 * branches reads as broken at the TPIN level).
 */
export function CountStatic({
  count,
  statusKey,
  statusLabel,
  noun = 'invoices',
  scope,
}: {
  count: number
  statusKey: 'synced' | 'failed' | 'pending'
  statusLabel: string
  /** Verb for the accessible name (defaults to "invoices"). */
  noun?: string
  /** Scope the count is reporting on (TPIN, deployment, etc.). */
  scope: string
}) {
  const emphasise = shouldEmphasiseFailed(statusKey, count)
  const classes = ['count-static', `count-${statusKey}`, emphasise ? 'is-emphasis' : '']
    .filter((c) => c !== '')
    .join(' ')
  const ariaLabel = `${statusLabel} — ${count} ${noun} for ${scope}`
  return (
    <span
      className={classes}
      data-testid={`count-static-${statusKey}`}
      aria-label={ariaLabel}
    >
      <span className="count-value">{count}</span>
      <span className="count-label">{statusLabel}</span>
    </span>
  )
}